import React, { useState, useEffect } from "react";
import axiosClient from "../../config/axiosClient";
import API_ENDPOINTS from "../../config/api";
import DepartmentModal from "./DepartmentModal";

const PAGE_SIZE = 10;

const DepartmentList = () => {
  const [departments, setDepartments] = useState([]);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    try {
      const res = await axiosClient.get(API_ENDPOINTS.departments);
      const safeData = res.data.map((d) => ({
        id: d.id,
        departmentCode: d.departmentCode || "",
        name: d.name || "",
      }));
      setDepartments(safeData);
    } catch (error) {
      console.error("❌ Error loading departments:", error);
    }
  };

  // --- Export Excel Logic ---
  const handleExport = async () => {
    try {
      const response = await axiosClient.get(`${API_ENDPOINTS.departments}/export`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Departments_${new Date().getTime()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert("Export failed!");
    }
  };

  // --- Import Excel Logic ---
  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("File", file); 

    try {
      const res = await axiosClient.post(`${API_ENDPOINTS.departments}/import`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data.success) {
        alert(`Successfully imported ${res.data.inserted} departments!`);
        fetchDepartments();
      }
      e.target.value = null; 
    } catch (error) {
      alert("Import error: " + (error.response?.data?.error || "Unknown error"));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this department?")) return;
    try {
      await axiosClient.delete(`${API_ENDPOINTS.departments}/${id}`);
      setDepartments(departments.filter((d) => d.id !== id));
    } catch (error) {
      alert(error.response?.data || "Cannot delete department");
    }
  };

  // --- 🔥 MỚI: Logic Delete All ---
  const handleDeleteAll = async () => {
    if (departments.length === 0) return;
    
    const confirmFirst = window.confirm("WARNING: Are you sure you want to delete ALL departments?");
    if (!confirmFirst) return;
    
    const confirmSecond = window.confirm("This action cannot be undone. Confirm one last time?");
    if (!confirmSecond) return;

    try {
      // Giả định API hỗ trợ DELETE không truyền ID hoặc endpoint đặc biệt
      // Nếu API của đại ca cần vòng lặp thì báo mình, nhưng tốt nhất nên dùng 1 request delete all
      await axiosClient.delete(`${API_ENDPOINTS.departments}/delete-all`); 
      setDepartments([]);
      setCurrentPage(1);
      alert("All departments have been deleted.");
    } catch (error) {
      alert(error.response?.data || "Error deleting all departments");
    }
  };

  const handleSave = async (data) => {
    try {
      if (editingDepartment) {
        await axiosClient.put(`${API_ENDPOINTS.departments}/${editingDepartment.id}`, data);
      } else {
        await axiosClient.post(API_ENDPOINTS.departments, data);
      }
      await fetchDepartments();
      setShowModal(false);
      setEditingDepartment(null);
    } catch (error) {
      alert("Error saving department!");
    }
  };

  const filteredData = departments.filter(
    (d) =>
      d.name.toLowerCase().includes(search.toLowerCase()) ||
      d.departmentCode.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(filteredData.length / PAGE_SIZE);
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const currentData = filteredData.slice(startIndex, startIndex + PAGE_SIZE);

  const thStyle = { backgroundColor: "#f8f9fc", color: "#4e73df", fontWeight: "bold", fontSize: "0.85rem", textTransform: "uppercase" };

  return (
    <div className="container-fluid py-4">
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <h1 className="h4 mb-0 text-gray-800 fw-bold">DEPARTMENT LIST</h1>
      </div>

      <div className="card shadow-sm border-0">
        <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center flex-wrap gap-3">
          <h6 className="m-0 font-weight-bold text-primary">Department Management</h6>

          <div className="d-flex align-items-center gap-2">
            <div className="position-relative">
              <input
                type="text"
                className="form-control form-control-sm ps-4"
                style={{ width: "200px", borderRadius: "20px" }}
                placeholder="Search..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
              />
              <i className="fas fa-search position-absolute text-muted" style={{ left: "12px", top: "8px", fontSize: "12px" }}></i>
            </div>

            <div className="btn-group shadow-sm">
              <button className="btn btn-sm btn-light border" onClick={handleExport} title="Export Excel">
                <i className="fas fa-file-export text-primary"></i>
              </button>
              <label className="btn btn-sm btn-light border mb-0" style={{ cursor: "pointer" }} title="Import Excel">
                <i className="fas fa-file-import text-warning"></i>
                <input type="file" accept=".xlsx" hidden onChange={handleImport} />
              </label>
            </div>

            {/* 🔥 NÚT DELETE ALL MỚI */}
            <button 
              className="btn btn-sm btn-outline-danger px-3 rounded-pill shadow-sm fw-bold"
              onClick={handleDeleteAll}
              disabled={departments.length === 0}
            >
              <i className="fas fa-trash-sweep me-1"></i> Delete All
            </button>

            <button
              className="btn btn-sm btn-success px-3 rounded-pill shadow-sm fw-bold"
              onClick={() => {
                setEditingDepartment(null);
                setShowModal(true);
              }}
            >
              <i className="fas fa-plus me-1"></i> Add New
            </button>
          </div>
        </div>

        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th className="text-center" style={{ ...thStyle, width: "80px" }}>No.</th>
                  <th style={thStyle}>Code</th>
                  <th style={thStyle}>Name</th>
                  <th className="text-center" style={{ ...thStyle, width: "150px" }}>Ops</th>
                </tr>
              </thead>
              <tbody>
                {currentData.length > 0 ? (
                  currentData.map((d, index) => (
                    <tr key={d.id}>
                      <td className="text-center text-muted small">{startIndex + index + 1}</td>
                      <td>
                        <span className="badge bg-light text-primary border px-2 py-1">
                          {d.departmentCode}
                        </span>
                      </td>
                      <td className="fw-bold text-dark">{d.name}</td>
                      <td className="text-center">
                        <div className="d-flex justify-content-center gap-2">
                          <button
                            className="btn btn-sm btn-outline-primary border-0 rounded-circle shadow-sm"
                            style={{ width: "32px", height: "32px", padding: "0" }}
                            onClick={async () => {
                              const res = await axiosClient.get(`${API_ENDPOINTS.departments}/${d.id}`);
                              setEditingDepartment(res.data);
                              setShowModal(true);
                            }}
                          >
                            <i className="fas fa-pencil-alt"></i>
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger border-0 rounded-circle shadow-sm"
                            style={{ width: "32px", height: "32px", padding: "0" }}
                            onClick={() => handleDelete(d.id)}
                          >
                            <i className="fas fa-trash-alt"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center py-5 text-muted">
                      No departments found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center p-3 bg-light border-top">
              <span className="small text-muted fw-bold">Page {currentPage} of {totalPages}</span>
              <nav>
                <ul className="pagination pagination-sm mb-0">
                  <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                    <button className="page-link border-0 shadow-sm mx-1 rounded-circle" onClick={() => setCurrentPage(currentPage - 1)}>
                      <i className="fas fa-chevron-left"></i>
                    </button>
                  </li>
                  <li className="page-item active">
                    <button className="page-link border-0 shadow-sm mx-1 rounded-circle bg-primary text-white">{currentPage}</button>
                  </li>
                  <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                    <button className="page-link border-0 shadow-sm mx-1 rounded-circle" onClick={() => setCurrentPage(currentPage + 1)}>
                      <i className="fas fa-chevron-right"></i>
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </div>
      </div>

      <DepartmentModal
        show={showModal}
        initialData={editingDepartment}
        onClose={() => { setShowModal(false); setEditingDepartment(null); }}
        onSave={handleSave}
      />
    </div>
  );
};

export default DepartmentList;